import React, { useRef, useEffect, useState } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { Briefcase, MessageCircle, Bell, Search, User } from 'lucide-react';
import PhoneShowcase from './PhoneShowcase';

// ─────────────────────────────────────────────────────────
// POSTER DATA
// ─────────────────────────────────────────────────────────
const posterFiles = [
  '/posters/Screenshot 2026-03-04 110342.png',
  '/posters/Screenshot 2026-03-04 110347.png',
  '/posters/Screenshot 2026-03-04 110353.png',
  '/posters/Screenshot 2026-03-04 110401.png',
  '/posters/Screenshot 2026-03-04 110406.png',
  '/posters/Screenshot 2026-03-04 110411.png',
  '/posters/Screenshot 2026-03-04 110418.png',
  '/posters/Screenshot 2026-03-04 110429.png',
];

// Wall pin positions — spread across a virtual corkboard
const wallPositions: { left: string; top: string; rotate: number; scale: number; pinColor: string }[] = [
  { left: '3%', top: '8%', rotate: -4, scale: 0.88, pinColor: '#ef4444' },
  { left: '22%', top: '4%', rotate: 3, scale: 0.85, pinColor: '#3b82f6' },
  { left: '42%', top: '6%', rotate: -2, scale: 0.90, pinColor: '#facc15' },
  { left: '62%', top: '3%', rotate: 5, scale: 0.82, pinColor: '#ef4444' },
  { left: '80%', top: '7%', rotate: -3, scale: 0.87, pinColor: '#22c55e' },
  { left: '10%', top: '45%', rotate: 4, scale: 0.86, pinColor: '#3b82f6' },
  { left: '54%', top: '42%', rotate: -5, scale: 0.84, pinColor: '#facc15' },
  { left: '75%', top: '48%', rotate: 2, scale: 0.89, pinColor: '#ef4444' },
];

// ─────────────────────────────────────────────────────────
// JOB DATA — India + International
// ─────────────────────────────────────────────────────────
const allJobs = [
  // International (Gulf / Abroad)
  { title: 'Industrial Welder', company: 'Al Rashed Group', flag: '🇸🇦', salary: '2,800 SAR', tag: 'Urgent', type: 'intl', color: '#ef4444' },
  { title: 'Civil Foreman', company: 'Gulf Stars LLC', flag: '🇦🇪', salary: '3,200 AED', tag: 'New', type: 'intl', color: '#22c55e' },
  { title: 'Electrician', company: 'Qatar Energy Co.', flag: '🇶🇦', salary: '4,000 QAR', tag: 'Hot', type: 'intl', color: '#f59e0b' },
  { title: 'Rigger / Scaffolder', company: 'Sky Manpower', flag: '🇩🇪', salary: '€2,100/mo', tag: 'New', type: 'intl', color: '#3b82f6' },
  // India
  { title: 'Factory Electrician', company: 'Tata Projects', flag: '🇮🇳', salary: '₹28,000/mo', tag: 'New', type: 'india', color: '#f97316' },
  { title: 'Construction Mason', company: 'L&T Heavy Eng.', flag: '🇮🇳', salary: '₹22,000/mo', tag: '450+ openings', type: 'india', color: '#8b5cf6' },
  { title: 'Pipe Fitter', company: 'HPCL Refinery', flag: '🇮🇳', salary: '₹32,000/mo', tag: 'Urgent', type: 'india', color: '#ef4444' },
];

// ─────────────────────────────────────────────────────────
// PHONE APP SCREEN — 2-phase animation
//   Phase 1: Poster Reel (story-style, auto-advance)
//   Phase 2: Job feed (India + International)
// ─────────────────────────────────────────────────────────




// ─────────────────────────────────────────────────────────
// SINGLE WALL POSTER (pinned to corkboard)
// ─────────────────────────────────────────────────────────
interface PosterProps {
  src: string;
  pos: typeof wallPositions[0];
  index: number;
  shouldFly: boolean;
}

const WallPoster: React.FC<PosterProps> = ({ src, pos, index, shouldFly }) => {
  const controls = useAnimation();

  useEffect(() => {
    if (shouldFly) {
      // staggered launch toward the phone (center of screen)
      controls.start({
        left: '50%',
        top: '50%',
        x: '-50%',
        y: '-50%',
        rotate: 0,
        scale: 0,
        opacity: 0,
        transition: {
          duration: 0.65,
          delay: index * 0.07,
          ease: [0.4, 0, 0.2, 1],
        },
      });
    } else {
      controls.set({
        left: pos.left,
        top: pos.top,
        x: '0%',
        y: '0%',
        rotate: pos.rotate,
        scale: pos.scale,
        opacity: 1,
      });
    }
  }, [shouldFly, controls, pos, index]);

  return (
    <motion.div
      animate={controls}
      initial={{
        left: pos.left,
        top: pos.top,
        x: '0%',
        y: '0%',
        rotate: pos.rotate,
        scale: pos.scale,
        opacity: 1,
      }}
      className="absolute origin-center pointer-events-none"
      style={{ willChange: 'transform, opacity' }}
    >
      {/* Corkboard push-pin */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
        <div
          className="w-4 h-4 rounded-full shadow-md border-2 border-white/40"
          style={{ background: pos.pinColor }}
        />
        <div className="w-1 h-2 bg-neutral-400 mx-auto rounded-b-full -mt-1" />
      </div>

      {/* Poster paper - Increased size */}
      <div
        className="w-[170px] md:w-[190px] rounded-lg overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.22)]"
        style={{ background: '#fffdf7' }}
      >
        <img
          src={src}
          alt={`Job poster ${index + 1}`}
          className="w-full h-auto block"
          loading="eager"
        />
      </div>

      {/* Bottom tape strip */}
      <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-12 h-2 bg-yellow-200/70 rounded-sm" />
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────
// MAIN POSTER WALL COMPONENT
// ─────────────────────────────────────────────────────────
const PosterWall: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'wall' | 'flying' | 'phone'>('wall');

  // Intersection Observer — trigger animation when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.2 && phase === 'wall') {
          // Let user see the wall for a second
          const t1 = setTimeout(() => setPhase('flying'), 1000);
          // Immediately show phone as the last poster starts moving (0.65s duration + offsets)
          const t2 = setTimeout(() => setPhase('phone'), 1000 + (8 * 70) + 150);
          return () => { clearTimeout(t1); clearTimeout(t2); };
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [phase]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#f5f0e8] border-t border-neutral-200"
      style={{ minHeight: '100vh' }}
    >
      {/* Corkboard texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 3px,
              rgba(139,109,56,0.06) 3px,
              rgba(139,109,56,0.06) 4px
            ),
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 3px,
              rgba(139,109,56,0.06) 3px,
              rgba(139,109,56,0.06) 4px
            )
          `,
        }}
      />
      {/* Edge shadow to give depth */}
      <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_80px_rgba(0,0,0,0.12)]" />

      {/* Section header */}
      <div className="relative z-20 pt-16 text-center px-6">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-500 mb-3"
        >
          Digital Transformation
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-black text-neutral-900 tracking-tight leading-tight mb-2"
          style={{ fontSize: 'clamp(30px, 4vw, 56px)', fontFamily: "'Inter', system-ui" }}
        >
          Wall se{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
            phone tak.
          </span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-neutral-500 text-[15px] max-w-lg mx-auto"
        >
          Ye kagaz ke posters ab digital ho gaye hain. Workers ab seedha apne phone pe jobs dekhte hain.
        </motion.p>
      </div>

      {/* ── CORKBOARD STAGE ── */}
      <div className="relative w-full" style={{ height: '68vh', minHeight: '420px' }}>

        {/* Posters mounted on wall */}
        {posterFiles.map((src, i) => (
          <WallPoster
            key={i}
            src={src}
            pos={wallPositions[i]}
            index={i}
            shouldFly={phase !== 'wall'}
          />
        ))}

        {/* ── PHONE MOCKUP (appears after posters fly in) ── */}
        <AnimatePresence>
          {phase === 'phone' && (
            <PhoneShowcase visible={phase === 'phone'} theme="recruiter" />
          )}
        </AnimatePresence>
      </div>

      {/* Bottom tagline */}
      <AnimatePresence>
        {phase === 'phone' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="relative z-20 text-center pb-16 px-6"
          >
            <p className="text-[18px] md:text-2xl font-black text-neutral-800 mb-2" style={{ fontFamily: "'Inter', system-ui" }}>
              Har poster, ab ek digital job listing.
            </p>
            <p className="text-neutral-500 text-[14px]">
              Workers ko ab deewar ke poster dhundne nahi padte.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PosterWall;
